import { useGlobalModal } from '@/hooks/useGlobalModal';
import { useSuspenseQuery } from '@apollo/client';
import GlobalModal from '@components/GlobalModal';
import ModalActions from '@components/modal/ModalActions';
import ModalContent from '@components/modal/ModalContent';
import ModalTitle from '@components/modal/ModalTitle';
import UserNameSpan from '@components/userNameSpan';
import { Access } from '@features/authorization';
import StandardAutocompleteIdsWithFallback from '@features/standardDesign/components/autocomplete/StandardAutocompleteIds';
import { msg, Plural, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Box, Skeleton, Typography } from '@mui/material';
import { PropsWithChildren, Suspense, useEffect, useMemo, useState, type FC } from 'react';
import { GET_ACTIVITY_LOGS } from '../graphql/queries/getActivityLogs';
import { HeatmapData } from '../types';
import { Heatmap } from './Heatmap';
import { useFormatters } from '@/hooks/useFormatters';

interface Props {
  userId: number;
}

const SuspenseComponent: FC<Props> = ({ userId }) => {
  const { formatRelativeTime } = useFormatters();
  const { data } = useSuspenseQuery(GET_ACTIVITY_LOGS, {
    variables: {
      filter: {
        accountIds: [userId],
        activityNames: ['access'],
      },
    },
  });

  const heatmapData: HeatmapData = useMemo(() => {
    return data.activityLogs.map((log) => [log.createdAt, 1]);
  }, [data]);

  const accessLast30Days = useMemo(() => {
    const now = Date.now();
    const limitTime = now - 30 * 24 * 60 * 60_000;

    const logs = data.activityLogs.filter((log) => log.createdAt > limitTime);
    return logs.length;
  }, [data]);

  const lastAccess = useMemo(() => {
    const lastLog = data.activityLogs.at(0);
    if (!lastLog) return <Trans>Nunca</Trans>;
    return formatRelativeTime(lastLog.createdAt);
  }, [data, formatRelativeTime]);

  return (
    <Box>
      <Box display="grid" gridTemplateColumns="repeat(3, 240px)" mb={4} top={0} zIndex={1} gap={4}>
        <StatsCard>
          <Typography lineHeight={1}>
            <Trans>Último acceso</Trans>
          </Typography>
          <Typography fontSize={24} fontWeight="bold">
            {lastAccess}
          </Typography>
        </StatsCard>
        <StatsCard>
          <Typography lineHeight={1}>
            <Trans>Últimos 30 días</Trans>
          </Typography>
          <Typography fontSize={24} fontWeight="bold">
            <Plural value={accessLast30Days} one="1 acceso" other="# accesos" />
          </Typography>
        </StatsCard>
      </Box>
      <Box display="grid" gridTemplateColumns="minmax(0,1fr)">
        <Heatmap data={heatmapData} />
      </Box>
    </Box>
  );
};

const StatsCard: FC<PropsWithChildren> = ({ children }) => {
  return (
    <Box
      sx={{
        bgcolor: 'white',
        border: 3,
        borderColor: '#00000052',
        color: 'black',
        borderRadius: 2,
        textAlign: 'center',
        p: 2,
        display: 'grid',
        alignContent: 'center',
      }}
    >
      {children}
    </Box>
  );
};

const NoUserSelected: FC = () => {
  return (
    <Box>
      <Typography>Selecciona un usuario</Typography>
    </Box>
  );
};

export const UserActivityLogsModal: FC = () => {
  const { _ } = useLingui();
  const { globalModalProps, state, onClose } = useGlobalModal('userActivityLogs');
  const [userId, setUserId] = useState<number | null>(null);

  useEffect(() => {
    setUserId(state.initialUserId ?? null);
  }, [state.initialUserId]);

  return (
    <GlobalModal
      {...globalModalProps}
      dialogProps={{ maxWidth: 'lg' }}
      authorization={{ access: Access.ViewActivityLogs }}
    >
      <ModalTitle
        onClose={onClose}
        title={<Trans>Registros de acceso</Trans>}
        subtitle={
          userId ? (
            <UserNameSpan
              userId={userId ?? undefined}
              variant="subtitle1"
              color="text.secondary"
              lineHeight={1}
            />
          ) : (
            <Trans>Seleccionar usuario</Trans>
          )
        }
      >
        <Suspense fallback={<Skeleton width="100%" height="40dvh" variant="rounded"></Skeleton>}>
          <StandardAutocompleteIdsWithFallback
            dataType="users"
            size="small"
            label={_(msg`Usuario`)}
            value={userId}
            onChange={(_, value) => setUserId(value)}
            sx={{ width: 300 }}
          />
        </Suspense>
      </ModalTitle>
      <ModalContent
        sx={{
          height: 600,
        }}
      >
        {state.open && (userId ? <SuspenseComponent userId={userId} /> : <NoUserSelected />)}
      </ModalContent>
      <ModalActions onClose={onClose} />
    </GlobalModal>
  );
};
